import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { storageService } from '../../../shared/services/storageService';
import secureStorage from '../../../shared/services/secureStorage';
import { timeZoneStorage } from '../api/timeZoneStorage';
import { TIMEZONE_STORAGE_KEY } from '../../../shared/store/userSlice';
import { AUTH_USER_ID_KEY } from '../api/userIdStorage';

// Configuration is now centralized here
const azureB2CConfig = {
  authority: 'https://vestigeexternalidtest.ciamlogin.com/c7910fd3-5e27-4449-86d2-ae2d7b11bcc9',
  client_id: '1212b811-eb40-4d6f-bd31-bb0197841da8',
  response_type: 'code',
  scope: 'openid profile email offline_access api://technician-app-bff/technician-bff.read.access',
  redirect_uri: window.location.origin + '/ShopHomePage',
  post_logout_redirect_uri: window.location.origin,
  loadUserInfo: false,
  monitorSession: false,
  metadata: {
    issuer: 'https://vestigeexternalidtest.ciamlogin.com/c7910fd3-5e27-4449-86d2-ae2d7b11bcc9/v2.0',
    authorization_endpoint: 'https://vestigeexternalidtest.ciamlogin.com/c7910fd3-5e27-4449-86d2-ae2d7b11bcc9/oauth2/v2.0/authorize',
    token_endpoint: 'https://vestigeexternalidtest.ciamlogin.com/c7910fd3-5e27-4449-86d2-ae2d7b11bcc9/oauth2/v2.0/token',
    end_session_endpoint: 'https://vestigeexternalidtest.ciamlogin.com/c7910fd3-5e27-4449-86d2-ae2d7b11bcc9/oauth2/v2.0/logout',
  },
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  // Use refresh_token grant for silent renewal (avoids iframe / third-party cookie issues).
  // Requires offline_access in the scope so Azure CIAM issues a refresh token.
  automaticSilentRenew: true,
  useRefreshTokens: true,
  // Fallback silent_redirect_uri for any iframe-based renewal path (not normally hit
  // when useRefreshTokens=true, but required by oidc-client-ts type contract).
  silent_redirect_uri: window.location.origin + '/silent-refresh.html',
};

// Export the singleton instance
export const userManager = new UserManager(azureB2CConfig);

console.log('[CIAM] userManager created with config:', {
  authority: azureB2CConfig.authority,
  client_id: azureB2CConfig.client_id,
  redirect_uri: azureB2CConfig.redirect_uri,
  scope: azureB2CConfig.scope,
});

// Event listener for when the access token is successfully renewed
userManager.events.addUserLoaded(async (user: User) => {
  console.log('[CIAM] addUserLoaded fired — user object:', user);
  console.log('[CIAM] access_token present:', !!user?.access_token);
  console.log('[CIAM] id_token present:', !!user?.id_token);
  console.log('[CIAM] expires_at:', user?.expires_at);
  if (user && user.access_token) {
    await storageService.setAuthToken(user.access_token);
    console.log('[CIAM] New access token saved to storage.');
  } else {
    console.warn('[CIAM] addUserLoaded fired but access_token is MISSING!');
  }
});

userManager.events.addSilentRenewError((error) => {
  console.error('[CIAM] Silent renew error:', error);
});

userManager.events.addUserSignedOut(() => {
  console.warn('[CIAM] User signed out event fired.');
});

userManager.events.addAccessTokenExpiring(() => {
  console.log('[CIAM] Access token is expiring soon...');
});

userManager.events.addAccessTokenExpired(() => {
  console.warn('[CIAM] Access token has expired.');
});

// Create a separate user manager for the password reset flow
const passwordResetConfig = {
  ...azureB2CConfig,
  // Per user feedback, align scope and add prompt=login for change password flow
  scope: 'openid profile email api://rider-bff/rider.api.access',
  extraQueryParams: {
    prompt: 'login',
  },
};
export const passwordResetUserManager = new UserManager(passwordResetConfig);

// Web stub — not called on web (Platform.OS === 'web' branch uses userManager.signinRedirect)
export const signinWithBrowser = (): Promise<never> =>
  Promise.reject(new Error('signinWithBrowser is not available on web'));

/**
 * Refreshes the access token on web using oidc-client-ts silent renew
 * (posts to the token endpoint via a hidden iframe using the refresh token
 * stored in sessionStorage by the UserManager).
 * Saves the new access token to storageService and returns it.
 * Returns null if the refresh fails (session truly expired).
 */
export const refreshAccessToken = async (): Promise<string | null> => {
  try {
    const user = await userManager.signinSilent();
    if (user?.access_token) {
      await storageService.setAuthToken(user.access_token);
      console.log('[CIAM] refreshAccessToken: silent renew succeeded');
      return user.access_token;
    }
    console.warn('[CIAM] refreshAccessToken: signinSilent returned no access_token');
    return null;
  } catch (err) {
    console.warn('[CIAM] refreshAccessToken: silent renew failed:', err);
    return null;
  }
};

/**
 * Signs out from Azure CIAM on web.
 * Clears oidc-client-ts session state and all stored tokens locally.
 * Does NOT call signoutRedirect() — navigation to AgencySelectionScreen is
 * handled by the caller (Header). The next signinRedirect() already carries
 * prompt=login (via extraQueryParams) so Azure CIAM will always show the
 * credential prompt regardless of any residual server-side session cookie.
 */
export const signoutWithBrowser = async (): Promise<void> => {
  await userManager.removeUser();
  await userManager.clearStaleState();
  await storageService.removeAuthToken();
  await storageService.removeRefreshToken();
  // Mark that the next sign-in must force credential re-entry (prompt=login).
  await storageService.setForceReauth();
  // Clear the persisted timezone and auth user ID so a subsequent login
  // gets fresh values and X-UserId is not carried over to the next session.
  timeZoneStorage.clear();
  await secureStorage.removeItem(TIMEZONE_STORAGE_KEY);
  await secureStorage.removeItem(AUTH_USER_ID_KEY);
  // Navigate to root so the deep-link config maps to AgencySelectionScreen ('')
  // instead of remaining on /ShopHomePage which would bypass the auth check.
  window.location.href = window.location.origin;
};